import React, { useState } from 'react';
import { Crown, Check, Sparkles, Users, Star, TrendingUp, Zap, Gift, Shield, Award, ChevronRight, X, ArrowRight, ArrowLeft, ShoppingBag, CheckCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useI18n } from '../i18n';
import { LOYALTY_PROGRAMS } from '../data/database';

// Icon mapping for dynamic rendering
const iconMap = { Crown, Users, Gift, TrendingUp, Zap, Shield, Star, Award, Sparkles };

const ElitProgramPage = ({ onBack, onNavigate }) => {
  const { user, userMode, activateElitPartner } = useApp();
  const { t } = useI18n();
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [showCheckout, setShowCheckout] = useState(false);

  // Get program configs
  const elitUserProgram = LOYALTY_PROGRAMS.elitUser;
  const elitPartnerProgram = LOYALTY_PROGRAMS.elitPartner;

  // User Elit Level (consumer view) - from database
  // Se l'utente non ha ancora un livello, mostra il progresso verso Bronze
  const userPurchases = user.elitPurchases || 0;
  const currentElitLevel = elitUserProgram.levels.find(l => userPurchases >= l.minPurchases) || null;
  const nextElitLevel = currentElitLevel 
    ? elitUserProgram.levels.find(l => l.level === currentElitLevel.level + 1)
    : elitUserProgram.levels[0]; // Se non ha livello, il prossimo è Bronze
  const purchasesForNext = nextElitLevel ? nextElitLevel.minPurchases - userPurchases : 0;
  const progressPercent = nextElitLevel 
    ? currentElitLevel 
      ? Math.max(0, ((userPurchases - currentElitLevel.minPurchases) / (nextElitLevel.minPurchases - currentElitLevel.minPurchases)) * 100)
      : Math.max(0, (userPurchases / nextElitLevel.minPurchases) * 100)
    : 100;

  // Consumer view - show user's Elit level and discounts
  if (userMode === 'consumer') {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        {/* Header */}
        <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 pt-12 pb-20 relative overflow-hidden">
          <div className="max-w-screen-2xl mx-auto px-6">
            <button onClick={onBack} className="absolute top-4 left-4 lg:left-6 w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center z-10">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="absolute inset-0 opacity-20 pointer-events-none">
              <div className="absolute top-10 right-10 w-40 h-40 bg-white rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-60 h-60 bg-yellow-300 rounded-full blur-3xl" />
            </div>
            <div className="relative text-center pt-6">
              <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">{currentElitLevel ? currentElitLevel.name : 'Programma Elit'}</h1>
              <p className="text-amber-100">Sconto attuale: <span className="font-bold text-white">{currentElitLevel ? currentElitLevel.discount : 0}%</span></p>
            </div>
          </div>
        </div>

        {/* Progress Card */}
        <div className="max-w-screen-2xl mx-auto px-6 -mt-10 relative z-10">
          <div className="bg-white rounded-2xl shadow-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500">Acquisti Elit effettuati</p>
                <p className="text-2xl font-bold text-gray-900">{userPurchases}</p>
              </div>
              {nextElitLevel && (
                <div className="text-right">
                  <p className="text-sm text-gray-500">Prossimo livello</p>
                  <p className="text-lg font-bold text-amber-500">{nextElitLevel.name}</p>
                </div>
              )}
            </div>
            
            {nextElitLevel ? (
              <>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                  <div 
                    className="bg-gradient-to-r from-amber-500 to-orange-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(progressPercent, 100)}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600 text-center">
                  Ancora <span className="font-bold text-amber-600">{purchasesForNext} acquisti</span> per raggiungere {nextElitLevel.name} ({nextElitLevel.discount}% sconto)
                </p>
              </>
            ) : (
              <div className="text-center py-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl">
                <p className="text-amber-700 font-medium">Hai raggiunto il livello massimo!</p>
              </div>
            )}
          </div>
        </div>

        {/* All Elit Levels - Dynamic with benefits */}
        <div className="max-w-screen-2xl mx-auto px-6 mt-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Tutti i livelli Elit</h2>
          <div className="space-y-3">
            {elitUserProgram.levels.map((level) => (
              <div 
                key={level.level}
                className={`bg-white rounded-xl p-4 relative ${currentElitLevel && level.level === currentElitLevel.level ? 'ring-2 ring-amber-500' : ''}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${currentElitLevel && level.level === currentElitLevel.level ? 'bg-amber-100' : 'bg-gray-100'}`}>
                      <Crown className={`w-5 h-5 ${currentElitLevel && level.level === currentElitLevel.level ? 'text-amber-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{level.name}</p>
                      <p className="text-sm text-gray-500">{level.minPurchases}+ acquisti</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-amber-600">{level.discount}%</p>
                    <p className="text-xs text-gray-500">sconto</p>
                  </div>
                </div>
                {/* Benefits from database */}
                <div className="pl-13 space-y-1">
                  {level.benefits?.map((benefit, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
                {currentElitLevel && level.level === currentElitLevel.level && (
                  <div className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                    Attuale
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* How to earn */}
        <div className="max-w-screen-2xl mx-auto px-6 mt-6">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4">
            <h3 className="font-semibold text-amber-900 mb-2">Come salire di livello</h3>
            <p className="text-sm text-amber-700">
              Ogni acquisto effettuato presso attività con il badge <span className="font-bold">Elit</span> conta per il tuo livello. 
              Cerca il badge Elit nelle attività per accumulare acquisti e sbloccare sconti sempre maggiori!
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-screen-2xl mx-auto px-6 mt-6">
          <button 
            onClick={() => onNavigate('search-results')}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-2 shadow-lg shadow-amber-500/30 hover:shadow-xl transition-all"
          >
            Scopri attività Elit
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // Check if user is already Elit Partner (partner view)
  if (user.isElitPartner) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        {/* Elit Member Header */}
        <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 pt-12 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute top-10 right-10 w-40 h-40 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-yellow-300 rounded-full blur-3xl" />
          </div>
          <div className="relative text-center">
            <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-3xl flex items-center justify-center mx-auto mb-4">
              <Crown className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t('elit', 'youAreElitPartner')}</h1>
            <p className="text-amber-100">{t('elit', 'enjoyExclusiveBenefits')}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="max-w-screen-2xl mx-auto px-6 -mt-10 relative z-10">
          <div className="bg-white rounded-2xl shadow-lg p-5 grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-500">+40%</p>
              <p className="text-xs text-gray-500">{t('elit', 'visibility')}</p>
            </div>
            <div className="text-center border-x border-gray-100">
              <p className="text-2xl font-bold text-emerald-500">+25%</p>
              <p className="text-xs text-gray-500">{t('elit', 'bookingsLabel')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-500">4.9</p>
              <p className="text-xs text-gray-500">{t('elit', 'avgRating')}</p>
            </div>
          </div>
        </div>

        {/* Benefits Status - Using dynamic data from database */}
        <div className="p-5 mt-4">
          <h3 className="font-semibold text-gray-900 mb-4">{t('elit', 'activeBenefits')}</h3>
          <div className="space-y-3">
            {elitPartnerProgram.partnerBenefits.map((benefit, i) => {
              const IconComponent = iconMap[benefit.icon] || Crown;
              return (
                <div key={i} className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm">
                  <div className={`w-12 h-12 bg-${benefit.color}-100 rounded-xl flex items-center justify-center`}>
                    <IconComponent className={`w-6 h-6 text-${benefit.color}-500`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{benefit.title}</h4>
                    <p className="text-sm text-gray-500">{benefit.description}</p>
                  </div>
                  <Check className="w-5 h-5 text-emerald-500" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Subscription Info */}
        <div className="max-w-screen-2xl mx-auto px-6 mt-4">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-amber-600">{t('elit', 'activePlan')}</p>
                <p className="font-bold text-amber-900">{t('elit', 'elitYearly')}</p>
              </div>
              <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-3 py-1 rounded-full">{t('profile', 'active').toUpperCase()}</span>
            </div>
            <p className="text-sm text-amber-700">{t('elit', 'nextRenewal')}: 11 Gennaio 2027</p>
          </div>
        </div>
      </div>
    );
  }

  // Partner view - Not yet Elit (signup flow)
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 pt-12 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-10 right-10 w-40 h-40 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-yellow-300 rounded-full blur-3xl" />
        </div>
        
        <button onClick={onBack} className="absolute top-4 left-4 p-2 text-white/80 hover:text-white z-10">
          <X className="w-6 h-6" />
        </button>

        <div className="relative text-center">
          <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-3xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Crown className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('elit', 'elitProgram')}</h1>
          <p className="text-amber-100">{t('elit', 'takeToNextLevel')}</p>
        </div>
      </div>

      {/* Benefits - Dynamic from database */}
      <div className="max-w-screen-2xl mx-auto px-6 -mt-12 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            {t('elit', 'exclusiveBenefits')}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {elitPartnerProgram.partnerBenefits.map((benefit, i) => {
              const IconComponent = iconMap[benefit.icon] || Crown;
              return (
                <div key={i} className="p-3 rounded-xl bg-gray-50">
                  <div className={`w-10 h-10 bg-${benefit.color}-100 rounded-xl flex items-center justify-center mb-2`}>
                    <IconComponent className={`w-5 h-5 text-${benefit.color}-500`} />
                  </div>
                  <h4 className="font-medium text-gray-900 text-sm">{benefit.title}</h4>
                  <p className="text-xs text-gray-500 mt-1">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Testimonials - Dynamic from database */}
      <div className="max-w-screen-2xl mx-auto px-6 mt-6">
        <h3 className="font-semibold text-gray-900 mb-4">{t('elit', 'theySayAboutUs')}</h3>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 scrollbar-hide">
          {elitPartnerProgram.testimonials.map((testimonial, i) => (
            <div key={i} className="min-w-[280px] bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
                <div>
                  <p className="font-medium text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.business}</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm">"{testimonial.text}"</p>
              <div className="flex mt-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing - Dynamic from database */}
      <div className="max-w-screen-2xl mx-auto px-6 mt-6">
        <h3 className="font-semibold text-gray-900 mb-4">{t('elit', 'chooseYourPlan')}</h3>
        <div className="space-y-3">
          {elitPartnerProgram.partnerPlans.map((plan) => (
            <button
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`w-full p-4 rounded-2xl border-2 transition-all text-left relative ${
                selectedPlan === plan.id 
                  ? 'border-amber-500 bg-amber-50' 
                  : 'border-gray-200 bg-white'
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-2 right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {t('elit', 'mostPopular')}
                </span>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{plan.name}</p>
                  {plan.savings && (
                    <p className="text-sm text-emerald-600">{t('elit', 'savePerYear', { amount: plan.savings })}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">€{plan.price}<span className="text-sm font-normal text-gray-500">{plan.period}</span></p>
                  {plan.total && (
                    <p className="text-xs text-gray-500">{t('elit', 'totalPerYear', { amount: plan.total })}</p>
                  )}
                </div>
              </div>
              <div className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 ${
                selectedPlan === plan.id ? 'border-amber-500 bg-amber-500' : 'border-gray-300'
              }`}>
                {selectedPlan === plan.id && <Check className="w-4 h-4 text-white" />}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-screen-2xl mx-auto px-6 mt-6">
        <button 
          onClick={() => setShowCheckout(true)}
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-2 shadow-lg shadow-amber-500/30 hover:shadow-xl transition-all"
        >
          {t('elit', 'becomeElitPartner')}
          <ArrowRight className="w-5 h-5" />
        </button>
        <p className="text-center text-sm text-gray-500 mt-3">
          {t('elit', 'moneyBackGuarantee')}
        </p>
      </div>

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end">
          <div className="bg-white w-full rounded-t-3xl p-5 animate-slide-up max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">{t('elit', 'completePurchase')}</h3>
              <button onClick={() => setShowCheckout(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Crown className="w-8 h-8 text-amber-500" />
                  <div>
                    <p className="font-semibold text-amber-900">Elit {elitPartnerProgram.partnerPlans.find(p => p.id === selectedPlan)?.name}</p>
                    <p className="text-sm text-amber-700">{t('elit', 'accessToAllBenefits')}</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-amber-900">
                  €{elitPartnerProgram.partnerPlans.find(p => p.id === selectedPlan)?.total || elitPartnerProgram.partnerPlans.find(p => p.id === selectedPlan)?.price}
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <input
                type="text"
                placeholder={t('payments', 'nameOnCard')}
                className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-amber-500"
              />
              <input
                type="text"
                placeholder={t('payments', 'cardNumber')}
                className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-amber-500"
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="MM/AA"
                  className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-amber-500"
                />
                <input
                  type="text"
                  placeholder="CVV"
                  className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>

            <button 
              onClick={() => {
                activateElitPartner();
                setShowCheckout(false);
              }}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white py-4 rounded-xl font-semibold"
            >
              {t('elit', 'payNow')}
            </button>
            
            <p className="text-center text-xs text-gray-500 mt-4">
              {t('elit', 'secureSSL')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ElitProgramPage;
